
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { TrainerCalendar } from "@/components/trainer-view/trainer-calendar"
import { TrainerClients } from "@/components/trainer-view/trainer-clients"
import { TrainerProfile } from "@/components/trainer-view/trainer-profile"
import { Calendar, Users, User, X } from "lucide-react"
import { useMobile } from "@/contexts/mobile-context"
import { Button } from "@/components/ui/button"
import { ScrollArea } from "@/components/ui/scroll-area"

export function TrainerView() {
  const { t } = useTranslation()
  const [activeView, setActiveView] = useState("calendar")
  const { isMobile, isSidebarOpen, closeSidebar } = useMobile()

  const navItems = [
    {
      title: t("trainerView.tabs.calendar"),
      href: "calendar",
      icon: Calendar,
    },
    {
      title: t("trainerView.tabs.clients"),
      href: "clients",
      icon: Users,
    },
    {
      title: t("trainerView.tabs.profile"),
      href: "profile",
      icon: User,
    },
  ]

  const handleNavigation = (href: string) => {
    setActiveView(href)
    if (isMobile) {
      closeSidebar()
    }
  }

  const renderContent = () => {
    switch (activeView) {
      case "calendar":
        return <TrainerCalendar />
      case "clients":
        return <TrainerClients />
      case "profile":
        return <TrainerProfile />
      default:
        return <TrainerCalendar />
    }
  }

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Mobile sidebar */}
      {isMobile && isSidebarOpen && (
        <div className="fixed inset-y-0 left-0 z-50 w-64 bg-background border-r">
          <div className="flex flex-col h-full">
            <div className="flex items-center justify-between p-4 border-b">
              <div className="font-bold text-xl">{t("trainerView.title")}</div>
              <Button variant="ghost" size="icon" onClick={closeSidebar}>
                <X className="h-4 w-4" />
              </Button>
            </div>
            <ScrollArea className="flex-1">
              <nav className="grid gap-1 p-2">
                {navItems.map((item) => (
                  <Button
                    key={item.href}
                    variant={activeView === item.href ? "secondary" : "ghost"}
                    className="justify-start"
                    onClick={() => handleNavigation(item.href)}
                  >
                    <item.icon className="mr-2 h-4 w-4" />
                    {item.title}
                  </Button>
                ))}
              </nav>
            </ScrollArea>
          </div>
        </div>
      )}

      {/* Desktop sidebar */}
      {!isMobile && (
        <div className="shrink-0 w-64 border-r bg-background">
          <div className="flex h-full flex-col">
            <div className="flex h-14 items-center border-b px-4">
              <div className="font-bold text-xl">{t("trainerView.title")}</div>
            </div>
            <ScrollArea className="flex-1">
              <nav className="grid gap-1 p-2">
                {navItems.map((item) => (
                  <Button
                    key={item.href}
                    variant={activeView === item.href ? "secondary" : "ghost"}
                    className="justify-start"
                    onClick={() => handleNavigation(item.href)}
                  >
                    <item.icon className="mr-2 h-4 w-4" />
                    {item.title}
                  </Button>
                ))}
              </nav>
            </ScrollArea>
          </div>
        </div>
      )}

      <div className="flex flex-col flex-1 overflow-hidden">
        <main className="flex-1 overflow-y-auto p-4 md:p-6">
          <div className="max-w-5xl mx-auto">
            {!isMobile && <h1 className="text-3xl font-bold mb-8">{t("trainerView.welcomeMessage")}</h1>}
            {renderContent()}
          </div>
        </main>
      </div>
    </div>
  )
}
